import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { ArrowLeft, Crown, Check, Lock, Beer } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const Subscription = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan: "annual",
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  const features = [
    { title: "Private Routes", desc: "Keep your favorite routes to yourself", free: false },
    { title: "Unlimited Trips", desc: "Plan as many rides as you want", free: true },
    { title: "CSV/GPX Export", desc: "Export routes for GPS devices", free: true },
    { title: "Stop Reviews", desc: "Read and write stop reviews", free: true },
    { title: "Route Sharing", desc: "Share public routes with others", free: true },
    { title: "Priority Support", desc: "Get help when you need it", free: false },
  ];

  return (
    <div className="mobile-container asphalt-texture min-h-screen pb-6">
      {/* Header */}
      <div className="p-4 pt-6">
        <div className="flex items-center gap-4 mb-6">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-oswald text-xl font-bold uppercase">Go Premium</h1>
          </div>
        </div>

        {/* Premium Badge */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-yellow-500/20 via-bronze/10 to-yellow-500/20 p-6 mb-6 border border-yellow-500/30">
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-yellow-500/10 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
                <Crown className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="font-oswald text-2xl font-bold uppercase">Premium</h2>
                <p className="text-yellow-500/80 text-sm">Unlock all features</p>
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-oswald text-4xl font-bold">$49.99</span>
              <span className="text-gray-400">/year</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">That's less than $5/month</p>
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-3 mb-8">
          <h3 className="font-oswald uppercase text-sm text-gray-400 tracking-wide">What you get</h3>
          {features.map((feature, idx) => (
            <div key={idx} className="glass rounded-xl p-4 flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                feature.free ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
              }`}>
                {feature.free ? <Check className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{feature.title}</p>
                <p className="text-xs text-gray-500">{feature.desc}</p>
              </div>
              {!feature.free && (
                <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-500 rounded-full uppercase font-bold">
                  Premium
                </span>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        {user?.is_premium ? (
          <div className="glass rounded-xl p-6 text-center">
            <Crown className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
            <h3 className="font-oswald text-lg uppercase mb-2">You're Premium!</h3>
            <p className="text-gray-500 text-sm">Enjoy all the features</p>
          </div>
        ) : (
          <Button
            data-testid="subscribe-btn"
            className="w-full h-14 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-oswald text-lg uppercase tracking-wider btn-primary"
            onClick={handleSubscribe}
            disabled={loading}
          >
            {loading ? (
              <div className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full" />
            ) : (
              <>
                <Crown className="w-5 h-5 mr-2" />
                Get Premium Now
              </>
            )}
          </Button>
        )}

        {/* Security Note */}
        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
          <Lock className="w-3 h-3" />
          <span>Secure payment via Stripe</span>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
